import React, { useContext, useState } from "react";
import _ from 'lodash';
import cx from 'classnames';

import { CollectionAsset, CollectionEditState } from "src/types/collections";
import { MenuState, SectionState } from "../../types";
import useMeasure from "react-use-measure";
import { animated, useChain, useSpringRef, useTransition } from "@react-spring/web";
import { BannerView } from "src/components/banner";
import { BannerEditor } from "../../components/banner/library";
import { BannerFilters } from "../../components/banner/filter";
import { CropEditor } from "../../components/crop";
import { CollectionContent } from "../../components/content";
import { DraggingSectionCard, SectionBar } from "./sections";
import { BentoLayout } from "./layout";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { DraggingAssetCard } from "./card";
import { fixCursorSnapOffset } from "src/utils/dnd";
import { snapCenterToCursor } from "@dnd-kit/modifiers";

import 'gridstack/dist/gridstack.css';
import 'gridstack/dist/gridstack.min.css';
import 'gridstack/dist/gridstack-extra.min.css';
import styles from './bento.module.scss';
import { CollectionContext } from "../../components/context";
import { CollectionName } from "../../components/name";

interface BentoExperienceProps {
  collection: CollectionEditState;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

interface DraggingAssetState {
  asset: CollectionAsset;
  overSection: boolean;
}

const BentoExperience: React.FC<BentoExperienceProps> = ({ collection, menuState, onMenuChange }) => {
  const collectionContext = useContext(CollectionContext);

  const showAssets = menuState.primaryAction === 'view' || menuState.primaryAction === 'edit';
  const showBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === null;
  const cropBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'crop';
  const showFilters = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'filter';

  const [headerRef, headerBounds] = useMeasure();

  const banner = _.get(collection, 'banner');
  const [draggingAsset, setDraggingAsset] = useState<DraggingAssetState | null>(null);
  const [draggingSection, setDraggingSection] = useState<SectionState | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  })

  const sensors = useSensors(
    pointerSensor
  );

  const assetsRef = useSpringRef();
  const assetsTransitions = useTransition(showAssets, {
    ref: assetsRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: [{ opacity: 1, transform: 'translate3d(0, 0px, 0)' }, { transform: 'none', immediate: true }],
    leave: [{ transform: 'translate3d(0, 0px, 0)', immediate: true }, { opacity: 0, transform: 'translate3d(0, 30vh, 0)' }],
    onRest: () => {
      if (showAssets) {
        //assetsRef.set({ transform: 'none' });
      }
    }
  });

  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: {
      friction: 14,
      tension: 120,
    },
  });

  useChain([assetsRef, bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0]);

  const handleAssetDragStart = (event) => {
    const asset = event.active.data.current.asset;
    const section = event.active.data.current.section;

    if (asset) {
      setDraggingAsset({
        asset: asset,
        overSection: false
      });
    } else if (section) {
      setDraggingSection(section);
    }
  }

  const handleAssetDragMove = (event) => {
    const entityType = event.over?.data.current?.type;
    setDraggingAsset((prev) => {
      if (!prev) {
        return null;
      }

      return {
        ...prev,
        overSection: entityType === 'section'
      };
    });
  }

  const handleAssetDragEnd = () => {
    setDraggingAsset(null);
    setDraggingSection(null);
  }

  return (

    <div className={styles.layout}>
      <div className={styles.background} ref={headerRef}>
        <BannerView banner={banner} defaultBanner="/images/collections/background.jpeg" />
      </div>
      <div className={cx(styles.body, styles[collectionContext.entityType], {
        [styles.editing]: menuState.primaryAction === 'edit',
        [styles.cropping]: cropBanner
      })}>
        <DndContext sensors={sensors}
          collisionDetection={fixCursorSnapOffset}
          modifiers={[]}
          onDragStart={handleAssetDragStart}
          onDragMove={handleAssetDragMove}
          onDragEnd={handleAssetDragEnd}
        >
          {assetsTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <div className={styles.assets}>
                {menuState.primaryAction === 'edit' && collectionContext.entityType === 'conversation' &&
                  <CollectionName collection={collection} className={styles.collectionName} />
                }
                <CollectionContent
                  collection={collection}
                  sectionBar={SectionBar}
                  layout={BentoLayout}
                  menuState={menuState}
                  onMenuChange={onMenuChange}
                />
              </div>
            </animated.div>
          ))}

          {bannerEditorTransitions((style, item) => (
            item && <animated.div style={style} className={cx(styles.content, styles.bannerEditor)}>
              <BannerEditor collection={collection} />
            </animated.div>
          ))}

          {bannerFilterTransitions((style, item) => (
            item && <animated.div style={style} className={cx(styles.content, styles.bannerEditor)}>
              <BannerFilters collection={collection} />
            </animated.div>
          ))}

          {cropperTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <CropEditor enabled={cropBanner}
                collection={collection}
                menuState={menuState}
                onMenuChange={onMenuChange}
              />
            </animated.div>
          ))}

          <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null} adjustScale={false}>
            {draggingAsset && (
              <DraggingAssetCard asset={draggingAsset.asset} overSection={draggingAsset.overSection} />
            )}

            {draggingSection && (
              <DraggingSectionCard section={draggingSection} />
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

export { BentoExperience };